/**
 * "Aprender" (home del alumno): emblema de marca + progreso vivo (XP/racha) y la
 * lista de cursos en los que estás inscrito (local-first). Enlaza a la senda de
 * cada curso. Tono profesional Space Academy; el progreso se muestra sobrio.
 */
import { type Component, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { BookOpen, Compass, Flame, GraduationCap, Sparkles, Star } from "lucide-solid";
import type { Enrollment } from "@space/powersync";
import { Link } from "@/lib/u-router";
import { Skeleton } from "@/components/ui/skeleton";
import { Mascot } from "@/components/ui/mascot";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";
import { useI18n } from "@/providers/i18n-provider";
import { useCourseTree, useEnrollments, useLearnerStats } from "../../hooks/use-course-data";
import { ReviewSection } from "./review-section";

export const LearnView: Component = () => {
  const { t } = useI18n();
  const { query: enrollments } = useEnrollments();
  const stats = useLearnerStats();
  const active = () => (enrollments.data ?? []).filter((e) => !e.deleted_at);

  // Superficie CRÍTICA para la regla del "una sola vez": completar una lección
  // actualiza la matrícula (XP/progreso) y PowerSync reinvalida esta consulta
  // varias veces seguidas. Observando solo el booleano, la entrada no se repite
  // al volver de la lección.
  const isEntering = useListEntrance({
    scope: () => "learn:enrolled",
    hasItems: () => active().length > 0,
  });

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      {/* Hero con emblema de marca + stats de progreso */}
      <div class="space-mesh relative overflow-hidden rounded-3xl border border-border p-5 md:p-6">
        <div class="flex items-center gap-4">
          <Mascot mood="wave" class="h-16 w-16 shrink-0 animate-bob md:h-20 md:w-20" />
          <div class="min-w-0">
            <h1 class="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
              {t(msg`Let's learn!`)}
            </h1>
            <p class="text-sm text-muted-foreground">
              {t(msg`Your progress, your courses and your streak.`)}
            </p>
          </div>
          <Link
            to="/catalog"
            class="ml-auto hidden items-center gap-2 rounded-2xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground shadow-md shadow-accent/25 transition-transform hover:scale-[1.03] active:scale-95 sm:flex"
          >
            <Compass class="h-4 w-4" /> {t(msg`Explore`)}
          </Link>
        </div>

        <div class="mt-5 flex flex-wrap gap-3">
          <StatPill
            testid="learn-total-xp"
            icon={<Star class="h-4 w-4" />}
            value={stats.data?.total_xp ?? 0}
            label="XP"
            tone="xp"
          />
          <StatPill
            testid="learn-total-streak"
            icon={<Flame class="h-4 w-4" />}
            value={stats.data?.current_streak ?? 0}
            label={t(msg`day streak`)}
            tone="streak"
          />
          <StatPill
            testid="learn-course-count"
            icon={<GraduationCap class="h-4 w-4" />}
            value={active().length}
            label={t(msg`courses`)}
            tone="grape"
          />
        </div>
      </div>

      <Link
        to="/catalog"
        class="flex items-center justify-center gap-2 rounded-2xl bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground shadow-md shadow-accent/25 transition-transform active:scale-95 sm:hidden"
      >
        <Compass class="h-4 w-4" /> {t(msg`Explore the catalog`)}
      </Link>

      {/* Repaso espaciado (SRS): solo si hay lecciones vencidas. */}
      <ReviewSection />

      <Show
        when={!enrollments.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 3 })}>
              {() => <Skeleton class="h-20 w-full rounded-2xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={active().length > 0}
          fallback={
            <div
              class="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border p-14 text-center"
              data-testid="learn-empty"
            >
              <Mascot mood="think" class="mb-4 h-24 w-24" />
              <h3 class="font-display text-lg font-bold">{t(msg`You have no courses yet`)}</h3>
              <p class="mt-1 max-w-xs text-sm text-muted-foreground">
                {t(msg`Pick your first course from the catalog and start earning XP.`)}
              </p>
              <Link
                to="/catalog"
                class="mt-4 inline-flex items-center gap-2 rounded-2xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground shadow-md shadow-accent/25 transition-transform hover:scale-[1.03] active:scale-95"
              >
                <Sparkles class="h-4 w-4" /> {t(msg`Explore the catalog`)}
              </Link>
            </div>
          }
        >
          <div class="grid gap-3" data-testid="learn-course-list">
            <For each={active()}>
              {(e, index) => (
                <EnrolledCard enrollment={e} entering={isEntering()} index={index()} />
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};

// Texto en `--foreground` (AA en ambos modos); el tono vive en fondo/anillo.
// (Antes el pill XP usaba el foreground fijo del chip ámbar — ilegible en dark.)
const toneMap = {
  xp: "bg-joy-sun/20 text-foreground ring-joy-sun/40",
  streak: "bg-joy-flame/15 text-foreground ring-joy-flame/35",
  grape: "bg-accent/12 text-foreground ring-accent/30",
} as const;

const StatPill: Component<{
  testid: string;
  icon: JSX.Element;
  value: JSX.Element;
  label: string;
  tone: keyof typeof toneMap;
}> = (props) => (
  <div
    class={`flex items-center gap-2 rounded-2xl px-4 py-2 ring-1 ring-inset ${toneMap[props.tone]}`}
    data-testid={props.testid}
  >
    <span class="opacity-90">{props.icon}</span>
    <span class="font-display text-xl font-extrabold leading-none">{props.value}</span>
    <span class="text-xs font-medium opacity-80">{props.label}</span>
  </div>
);

const EnrolledCard: Component<{ enrollment: Enrollment; entering: boolean; index: number }> = (
  props,
) => {
  const { t } = useI18n();
  const courseId = () => props.enrollment.course_id ?? undefined;
  const { course } = useCourseTree(courseId);
  const entrance = () => listEntranceProps(props.entering, props.index);
  return (
    <Link
      to="/learn/$courseId"
      params={{ courseId: props.enrollment.course_id ?? "" }}
      // Lista explícita en lugar de `transition-all`: nada que fuerce layout.
      class={`group flex items-center gap-4 rounded-2xl border border-border bg-surface p-4 shadow-sm transition-[transform,border-color,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md active:scale-[0.99] ${entrance().class ?? ""}`}
      style={entrance().style}
      data-testid="enrolled-card"
    >
      <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-sm transition-transform group-hover:scale-105">
        <BookOpen class="h-5 w-5" />
      </div>
      <span class="font-display font-bold">{course.data?.title ?? t(msg`Course`)}</span>
      <span class="ml-auto text-accent opacity-0 transition-opacity group-hover:opacity-100">
        {t(msg`Continue`)} →
      </span>
    </Link>
  );
};
